import { AlertTriangle, CheckCircle2, Clock, User } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import ApplicantRecruitmentCommunicationSummary from './ApplicantRecruitmentCommunicationSummary';
import { LabeledProgressBadge } from '../compliance/LabeledProgressMetrics';
import { resolveApplicantProfileProgress } from '../../lib/profileProgress';

const STATUS_STYLES = {
  action_required_from_applicant: 'border-amber-300 bg-amber-50',
  awaiting_admin_review: 'border-blue-200 bg-blue-50',
  onboarding_complete: 'border-green-200 bg-green-50',
  in_progress: 'border-slate-200 bg-slate-50',
};

function formatStepId(stepId) {
  return String(stepId || 'profile checks').replace(/_/g, ' ');
}

export default function AdminOnboardingSyncPanel({
  onboardingSync,
  onRefresh,
  onJumpToTab,
  employmentSignedOff = true,
  recruitmentApproved = false,
  recruitmentProgress = null,
}) {
  if (!onboardingSync?.wizard_enabled) return null;

  const status = onboardingSync.applicant_status || 'in_progress';
  const panelClass = STATUS_STYLES[status] || STATUS_STYLES.in_progress;
  const applicantSnapshot = resolveApplicantProfileProgress(onboardingSync);
  const progress = applicantSnapshot
    ? {
        percentage: applicantSnapshot.percentage,
        completed_steps: applicantSnapshot.completed,
        total_steps: applicantSnapshot.total,
        current_step_number: onboardingSync.applicant_progress?.current_step_number,
      }
    : null;
  const adminActions = onboardingSync.admin_pending_actions || [];
  const workerActions = onboardingSync.worker_pending_actions || [];

  return (
    <details
      className="rounded-xl border border-slate-200 bg-white"
      data-testid="admin-onboarding-sync-disclosure"
    >
      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-800">
        Applicant journey: {progress ? `${progress.percentage}% submitted` : 'progress unavailable'}
        {recruitmentProgress && recruitmentProgress.status_unavailable !== true
          ? ` · Recruitment: ${recruitmentProgress.percentage ?? 0}% verified`
          : ''}
        {onboardingSync.forced && onboardingSync.next_worker_action ? ' · Waiting on applicant' : ''}
        <span className="ml-2 text-xs font-normal text-slate-500">View progress, messages &amp; details</span>
      </summary>
      <div
        className={`m-3 mt-0 rounded-xl border p-4 ${panelClass}`}
        data-testid="admin-onboarding-sync-panel"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              {status === 'onboarding_complete' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
              ) : status === 'awaiting_admin_review' ? (
                <Clock className="h-4 w-4 text-blue-600 shrink-0" />
              ) : status === 'action_required_from_applicant' ? (
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              ) : (
                <User className="h-4 w-4 text-slate-600 shrink-0" />
              )}
              Applicant Progress: {onboardingSync.applicant_status_label}
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {progress
                ? `Step ${progress.current_step_number || '—'} of ${progress.total_steps} · ${progress.percentage}% completed/submitted`
                : 'Progress temporarily unavailable — no work is assumed missing'}
              {onboardingSync.current_step_title
                ? ` · ${onboardingSync.current_step_title}`
                : onboardingSync.current_step_id
                  ? ` · ${formatStepId(onboardingSync.current_step_id)}`
                  : ''}
              {onboardingSync.job_role ? ` · ${onboardingSync.job_role}` : ''}
            </p>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="lifecycle-progress-metrics">
              {progress ? (
                <LabeledProgressBadge
                  metricType="applicant"
                  completed={progress.completed_steps}
                  total={progress.total_steps}
                  percentage={progress.percentage}
                  size="sm"
                />
              ) : null}
              {recruitmentProgress && recruitmentProgress.status_unavailable !== true ? (
                <LabeledProgressBadge
                  metricType="recruitment"
                  completed={recruitmentProgress.verified_count ?? 0}
                  total={recruitmentProgress.required_count ?? 0}
                  percentage={recruitmentProgress.percentage ?? 0}
                  size="sm"
                />
              ) : null}
            </div>
            <p className="mt-2 text-[11px] text-slate-600">
              Applicant Progress counts seven journey stages, including later onboarding. Recruitment is what Osabea has verified for Recruitment Approval.
            </p>
            {onboardingSync.forced && onboardingSync.next_worker_action ? (
              <p className="mt-2 text-xs text-amber-800">
                Waiting on applicant — {onboardingSync.next_worker_action.title}
                {onboardingSync.forced_step_id
                  ? ` (held at ${formatStepId(onboardingSync.forced_step_id)})`
                  : ''}
              </p>
            ) : null}
            {!employmentSignedOff ? (
              <p className="mt-2 text-xs text-amber-900">
                Employment Review sign-off is still required on tab <strong>1. Recruitment Review</strong> before final approval.
              </p>
            ) : recruitmentApproved ? (
              <p className="mt-2 text-xs text-green-800">
                Recruitment approved — onboarding continues. Activation still requires canonical readiness and a current Fit-for-Work decision.
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 gap-2">
            {onJumpToTab ? (
              <Button type="button" size="sm" variant="outline" onClick={() => onJumpToTab('employment')}>
                Open employment
              </Button>
            ) : null}
            {onRefresh ? (
              <Button type="button" size="sm" variant="ghost" onClick={onRefresh}>
                Refresh
              </Button>
            ) : null}
          </div>
        </div>

        {adminActions.length > 0 ? (
          <div className="mt-3 rounded-lg border border-blue-100 bg-white/80 p-3" data-testid="admin-pending-actions-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-800">Your review queue</p>
            <ul className="mt-2 space-y-1.5">
              {adminActions.slice(0, 6).map((action) => (
                <li key={action.key} className="flex items-start gap-2 text-xs text-blue-900">
                  <Badge variant="outline" className="shrink-0 border-blue-200 bg-blue-50 text-blue-700">
                    Admin
                  </Badge>
                  <span>{action.title}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {workerActions.length > 0 && !onboardingSync.forced ? (
          <div className="mt-3 rounded-lg border border-amber-100 bg-white/80 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Applicant must complete</p>
            <ul className="mt-2 space-y-1.5">
              {workerActions.slice(0, 4).map((action) => (
                <li key={action.key} className="text-xs text-amber-900">{action.title}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <div className="px-3 pb-3">
        <ApplicantRecruitmentCommunicationSummary />
      </div>
    </details>
  );
}
